import { isAxiosError } from 'axios';

import { DealsDashboardClient } from '@/shared/api';
import type { CancelDealErrorKind, CancelDealRequest } from './cancel-deal.types';

const cancelDealPath = (alphaId: string) =>
  `/v1/landlord/deals/${encodeURIComponent(alphaId)}/cancel`;

/** A failed cancel. `kind` drives the UI; `message` stays the transport message for monitoring. */
export class CancelDealError extends Error {
  readonly kind: CancelDealErrorKind;
  readonly status?: number;
  readonly code?: string;

  constructor(
    kind: CancelDealErrorKind,
    message: string,
    options?: { status?: number; code?: string; cause?: unknown },
  ) {
    super(message, { cause: options?.cause });
    this.name = 'CancelDealError';
    this.kind = kind;
    this.status = options?.status;
    this.code = options?.code;
  }
}

// DDS puts the machine-readable reason in `type`; 401/403 never get here because the client logs
// the user out.
const getCancelDealErrorKind = (status: number, type: unknown): CancelDealErrorKind => {
  if (status === 409 || type === 'deal_not_cancellable') {
    return 'not_cancellable';
  }
  if (status === 404) {
    return 'not_found';
  }
  if (status === 400 || status === 422) {
    return 'invalid';
  }
  return 'unavailable';
};

const toCancelDealError = (error: unknown): CancelDealError => {
  if (!isAxiosError<{ type?: unknown }>(error)) {
    return new CancelDealError(
      'unavailable',
      error instanceof Error ? error.message : 'Failed to cancel deal',
      { cause: error },
    );
  }
  const status = error.response?.status ?? 0;
  const type = error.response?.data?.type;
  return new CancelDealError(getCancelDealErrorKind(status, type), error.message, {
    status,
    code: typeof type === 'string' ? type : undefined,
    cause: error,
  });
};

/**
 * Cancels an invite (`inv_…`) or an Atlas deal (`sd_…`). DDS answers 204 with no body, so
 * success resolves to nothing.
 */
export const cancelDeal = async (alphaId: string, request: CancelDealRequest) => {
  try {
    await DealsDashboardClient.post(cancelDealPath(alphaId), request);
  } catch (error) {
    throw toCancelDealError(error);
  }
};
